// SpecialistInfoCard.jsx
import * as React from 'react';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Stethoscope,
  Languages,
  PlusCircle,
  ClipboardList,
  Building2,
  CalendarClock,
} from 'lucide-react';
import { Heading } from '@/components/ui/shared/heading';
import { Badge } from '@/components/ui/badge';
import { useCartData } from '../context/CartDataContext';
import { AdditionalServicesDialog } from './AdditionalServicesDialog';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';

import { useSettingsState } from '@/components/context/SettingsContext';

export default function SpecialistInfoCard() {
  const { service, otherServices, selectedTimeSlot, remainingTimeText } =
    useCartData();

  const { getDefaultSpecialistImage } = useSettingsState();

  const [showAdditionalDialog, setShowAdditionalDialog] = React.useState(false);

  const svc = Array.isArray(service) ? service[0] : service;
  if (!svc) return null;

  const hasSpecialist = Boolean(svc?.specialistId);

  const imageUrl = hasSpecialist
    ? `${process.env.REACT_APP_API_BASE}/Specialists/${svc.specialistId}/picture`
    : null;

  const name = svc?.specialistName || '';
  const specialty = svc?.specialty || svc?.serviceTypeTitle || '';
  const languages = svc?.languages || [];
  const serviceName = svc?.serviceTitle || '';
  const clinic = svc?.departmentName || '';
  const address = svc?.departmentAddress || '';
  const dateTimeISO = selectedTimeSlot?.startDate || '';

  const initials =
    (name || serviceName)
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || '?';

  const headerTitle = hasSpecialist ? name : serviceName;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Heading variant="3" className="flex-shrink min-w-0">
            Vizito informacija
          </Heading>
          {remainingTimeText && (
            <>
              <Badge variant="time" className="hidden md:inline ">
                Užsakymui pateikti liko: {remainingTimeText}
              </Badge>

              <Badge variant="time" className="md:hidden ">
                liko: {remainingTimeText}
              </Badge>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="w-full max-w-xl font-primary">
          {/* MOBILE HEADER */}
          <div className="sm:hidden">
            <div className="flex items-center gap-3">

              {/* 🔥 UPDATED MOBILE AVATAR */}
              <Avatar className="w-10 h-10">
                <AvatarImage
                  src={imageUrl || undefined}
                  alt={headerTitle}
                  className="object-cover"
                />
                <AvatarFallback>
                  <img
                    src={getDefaultSpecialistImage()}
                    alt="Default avatar"
                    className="w-full h-full object-cover"
                  />
                </AvatarFallback>
              </Avatar>

              <Heading variant="4">{headerTitle}</Heading>
            </div>

            <div className="mt-2">
              <InfoLines
                specialty={specialty}
                languages={languages}
                showAdditionalServices={
                  otherServices?.serviceShortInfo?.length > 0
                }
                onExtraClick={() => setShowAdditionalDialog(true)}
              />
            </div>
          </div>

          {/* DESKTOP HEADER */}
          <div className="hidden sm:flex flex-col lg:flex-row gap-4 items-start">
            <Avatar className="w-24 h-24 flex-shrink-0">
              <AvatarImage
                src={imageUrl || undefined}
                alt={headerTitle}
                className="object-cover"
              />
              <AvatarFallback>
                <img
                  src={getDefaultSpecialistImage()}
                  alt="Default avatar"
                  className="w-full h-full object-cover"
                />
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <Heading variant="4" className="break-words">
                {headerTitle}
              </Heading>
              <div className="mt-2">
                <InfoLines
                  specialty={specialty}
                  languages={languages}
                  showAdditionalServices={
                    !!otherServices?.serviceShortInfo?.length
                  }
                  onExtraClick={() => setShowAdditionalDialog(true)}
                />
              </div>
            </div>
          </div>

          <Separator className="my-5" />

          {/* DETAILS */}
          <div className="space-y-4">
            {hasSpecialist && (
              <DetailRow
                icon={<ClipboardList className="w-5 h-5 text-primary" />}
                label="Paslaugos pavadinimas"
                value={serviceName}
              />
            )}
            <DetailRow
              icon={<Building2 className="w-5 h-5 text-primary" />}
              label="Diagnostikos centras/klinika"
              value={`${clinic}${clinic && address ? ', ' : ''}${address}`}
            />
            <DetailRow
              icon={<CalendarClock className="w-5 h-5 text-primary" />}
              label="Diena ir laikas"
              value={formatLtDateTime(dateTimeISO)}
            />
          </div>

          <AdditionalServicesDialog
            open={showAdditionalDialog}
            onOpenChange={setShowAdditionalDialog}
            items={otherServices?.serviceShortInfo?.map((s) => ({
              title: s.title,
              price: s.price,
            }))}
          />
        </div>
      </CardContent>
      <CardFooter />
    </Card>
  );
}

function InfoLines({
  specialty,
  languages,
  showAdditionalServices,
  onExtraClick,
}) {
  return (
    <div className="space-y-1">
      {!!specialty && (
        <div className="flex items-start gap-2 text-sm">
          <Stethoscope className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
          <span>{specialty}</span>
        </div>
      )}

      {Array.isArray(languages) && languages.length > 0 && (
        <div className="flex items-start gap-2 text-sm">
          <Languages className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
          <span>Kalbos: {languages.join(', ')}</span>
        </div>
      )}

      

      {showAdditionalServices && (
        <div className="flex items-start gap-2 text-sm">
          <PlusCircle className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
          <span className="leading-5">
            Papildomos teikiamos paslaugos:{' '}
            <button
              type="button"
              className="text-[#1F4B81] underline underline-offset-2"
              onClick={
                onExtraClick || (() => console.log('Show extra services'))
              }
            >
              Rodyti
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1">
        <div className="text-sm text-[#6C6C7D]">{label}</div>
        <div className="text-[15px] leading-6">{value}</div>
      </div>
    </div>
  );
}

function formatLtDateTime(iso) {
  try {
    if (!iso) return '—';
    const d = new Date(iso);
    const datePart = new Intl.DateTimeFormat('lt-LT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
    const time = new Intl.DateTimeFormat('lt-LT', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
    return `${datePart}, ${time}`;
  } catch {
    return iso || '—';
  }
}
